import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tags, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { getXeroTracking, setInvoiceTracking, setLineTracking } from "@/lib/apiClient";

export const useXeroTracking = () => {
  const [t, setT] = useState(null);
  useEffect(() => { getXeroTracking().then(setT).catch(() => setT({ connected: false, categories: [] })); }, []);
  return t;
};

const Picker = ({ category, value, inherited, onChange, disabled, testId }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testId}
    className={cn("h-7 max-w-[180px] rounded-md border border-border bg-background px-1.5 text-xs", !value && inherited && "text-muted-foreground italic")}>
    <option value="">{inherited ? `${inherited} (invoice)` : "—"}</option>
    {category.options.map((o) => <option key={o.id || o.name} value={o.name}>{o.name}</option>)}
  </select>
);

export const TrackingPicker = ({ inv, user, tracking, onChanged }) => {
  const [busy, setBusy] = useState(false);
  if (!tracking?.connected || !tracking.categories.length || inv.status === "rejected") return null;
  const cur = inv.tracking || {};
  const save = (name, v) => {
    const next = { ...cur, [name]: v || null };
    setBusy(true);
    setInvoiceTracking(inv.id, next, user).then((u) => { onChanged(u); toast.success(v ? `${name} → ${v} for every line` : `${name} cleared`); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not save tracking")).finally(() => setBusy(false));
  };
  return (
    <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4" data-testid="xero-tracking-card">
      <div className="flex items-center gap-2 text-sm font-medium"><Tags className="h-4 w-4 text-sky-500" /> Xero tracking {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}</div>
      <p className="text-xs text-muted-foreground mt-1">Department or project for cost-centre reporting in Xero. Applies to every line unless a line overrides it{inv.tracking_source === "supplier_default" ? " · pre-filled from this supplier's last approval" : ""}.</p>
      <div className="mt-3 flex flex-wrap gap-4">
        {tracking.categories.map((c) => (
          <label key={c.id || c.name} className="flex flex-col gap-1 text-xs text-muted-foreground">{c.name}
            <Picker category={c} value={cur[c.name]} onChange={(v) => save(c.name, v)} disabled={busy} testId={`tracking-${c.name.replace(/\s+/g, "-").toLowerCase()}`} />
          </label>
        ))}
      </div>
      {inv.xero?.invoice_id && Object.keys(cur).length > 0 && !inv.xero.tracked && <p className="mt-2 text-[11px] text-amber-500" data-testid="tracking-resync-hint">Re-sync to Xero to apply tracking to the existing bill.</p>}
    </div>
  );
};

export const LineTrackingSelect = ({ inv, li, user, tracking, onChanged }) => {
  const [busy, setBusy] = useState(false);
  if (!tracking?.connected || !tracking.categories.length || inv.status === "rejected") return null;
  const own = li.tracking || {};
  const save = (name, v) => {
    const next = { ...own, [name]: v || null };
    setBusy(true);
    setLineTracking(inv.id, li.id, next, user).then(onChanged).catch((e) => toast.error(e?.response?.data?.detail || "Could not save tracking")).finally(() => setBusy(false));
  };
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`line-tracking-${li.id}`}>
      {tracking.categories.map((c) => (
        <Picker key={c.id || c.name} category={c} value={own[c.name]} inherited={(inv.tracking || {})[c.name]} onChange={(v) => save(c.name, v)} disabled={busy}
          testId={`line-tracking-${li.id}-${c.name.replace(/\s+/g, "-").toLowerCase()}`} />
      ))}
    </div>
  );
};
